/**
 * Czas trwania nagrania odczytany przez PRZEGLADARKE z metadanych pliku (element audio albo wideo), zanim cokolwiek
 * pojedzie na serwer. Sluzy do dwoch rzeczy: ostrzezenia o koszcie przy dlugim nagraniu i wczesnego odrzucenia
 * nagrania dluzszego niz limit serwera.
 *
 * To odczyt "z nadzieja", nie pomiar: format moze nie miec naglowka z czasem, przegladarka moze nie znac kodeka, a
 * dla MP3 bez naglowka VBR jest to szacunek z bitrate'u. Dlatego kazda porazka (blad, brak metadanych, czas
 * nieskonczony, przekroczenie limitu 3 s) to `null` - "nie wiemy" - a nigdy zgadywana liczba i nigdy blad dla uzytkownika.
 *
 * Srodowisko jest wstrzykiwane, zeby dalo sie to przetestowac bez przegladarki i bez prawdziwego pliku.
 */

/** Tyle czekamy na metadane. Dluzej = pomijamy ostrzezenie, formularz nie moze wisiec na dziwnym pliku. */
export const LIMIT_ODCZYTU_CZASU_MS = 3000;

/** Wycinek HTMLMediaElement, ktorego uzywamy. */
export interface ElementMedialny {
  readonly duration: number;
  preload: string;
  src: string;
  addEventListener(typ: "loadedmetadata" | "error", obsluga: () => void): void;
  removeAttribute(nazwa: string): void;
  load(): void;
}

export interface SrodowiskoOdczytuCzasu {
  utworzElement(rodzaj: "audio" | "video"): ElementMedialny;
  utworzUrl(plik: Blob): string;
  zwolnijUrl(url: string): void;
  ustawZegar(wykonaj: () => void, ms: number): unknown;
  zdejmijZegar(zegar: unknown): void;
}

/** Srodowisko prawdziwej przegladarki, albo null, gdy brakuje czegos, bez czego odczyt nie ma sensu (np. jsdom). */
function srodowiskoPrzegladarki(): SrodowiskoOdczytuCzasu | null {
  if (typeof document === "undefined" || typeof URL === "undefined") return null;
  if (typeof URL.createObjectURL !== "function" || typeof URL.revokeObjectURL !== "function") return null;
  return {
    utworzElement: (rodzaj) => document.createElement(rodzaj),
    utworzUrl: (plik) => URL.createObjectURL(plik),
    zwolnijUrl: (url) => URL.revokeObjectURL(url),
    ustawZegar: (wykonaj, ms) => setTimeout(wykonaj, ms),
    zdejmijZegar: (zegar) => clearTimeout(zegar as ReturnType<typeof setTimeout>),
  };
}

/** Skonczona liczba dodatnia albo null (strumienie bez dlugosci maja Infinity, uszkodzone NaN). */
function poprawnyCzas(duration: number): number | null {
  return Number.isFinite(duration) && duration > 0 ? duration : null;
}

export function odczytajCzasNagrania(
  plik: Pick<File, "type"> & Blob,
  opcje: { limitMs?: number; srodowisko?: SrodowiskoOdczytuCzasu | null } = {},
): Promise<number | null> {
  const srodowisko = opcje.srodowisko === undefined ? srodowiskoPrzegladarki() : opcje.srodowisko;
  if (!srodowisko) return Promise.resolve(null);
  const limitMs = opcje.limitMs ?? LIMIT_ODCZYTU_CZASU_MS;

  return new Promise<number | null>((resolve) => {
    let url: string | null = null;
    let element: ElementMedialny | null = null;
    let zegar: unknown = null;
    let skonczone = false;

    // Sprzatanie jest w jednym miejscu i wykonuje sie dokladnie raz, bez wzgledu na to, ktore zdarzenie wygralo:
    // adres obiektu trzyma plik w pamieci przegladarki, dopoki go nie zwolnimy.
    const zakoncz = (wynik: number | null): void => {
      if (skonczone) return;
      skonczone = true;
      if (zegar !== null) srodowisko.zdejmijZegar(zegar);
      try {
        element?.removeAttribute("src");
        element?.load();
      } catch {
        // Sprzatanie elementu nie moze zmienic wyniku odczytu.
      }
      try {
        if (url !== null) srodowisko.zwolnijUrl(url);
      } catch {
        // Jak wyzej.
      }
      resolve(wynik);
    };

    try {
      url = srodowisko.utworzUrl(plik);
      element = srodowisko.utworzElement(plik.type.startsWith("video/") ? "video" : "audio");
      element.preload = "metadata";
      const el = element;
      el.addEventListener("loadedmetadata", () => zakoncz(poprawnyCzas(el.duration)));
      el.addEventListener("error", () => zakoncz(null));
      zegar = srodowisko.ustawZegar(() => zakoncz(null), limitMs);
      el.src = url;
    } catch {
      zakoncz(null);
    }
  });
}
